import type { FundDetail } from "@/data/fund-detail-types";
import { cn } from "@/lib/utils";
/**
 * Canvas performance chart for leveraged + FDRS funds.
 *
 * Ported verbatim from dane `frontend/app/etf-website/components/leveraged-perf-chart.tsx`.
 * Adapted imports only (cn from @/lib/utils, FundDetail from @/data/fund-detail-types).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// ── Types ──────────────────────────────────────────────────────────────

interface DataPoint {
  date: Date;
  nav: number;
  mkt: number;
}

/** API-ready shape for performance chart data (date as ISO string). */
export interface PerformanceDataPoint {
  date: string;
  nav: number;
  mkt: number;
}

/** API-ready shape for performance history table rows. Each cell is a
 *  pre-formatted percent string ("+1.23%", "-13.32%", or "—" if unavailable
 *  / window predates inception). Mirrors static reference's perf-history-table. */
export interface PerformanceHistoryRow {
  label: string;
  oneMonth: string;
  threeMonth: string;
  ytd: string;
  oneYear: string;
  threeYear: string;
  fiveYear: string;
  sinceInceptionAnn: string;
  sinceInceptionCum: string;
}

type Period = "1M" | "3M" | "1Y" | "YTD" | "ALL";

const PERIODS: Period[] = ["1M", "3M", "1Y", "YTD", "ALL"];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ── Helpers ─────────────────────────────────────────────────────────────

function niceStep(range: number): number {
  const bases = [1, 2, 5];
  const mag = 10 ** Math.floor(Math.log10(range / 4));
  for (const b of bases) {
    const s = b * mag;
    if (range / s <= 8) return s;
  }
  return mag * 10;
}

function fmtDate(d: Date): string {
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

// ── Component ──────────────────────────────────────────────────────────

interface LeveragedPerfChartProps {
  fund: FundDetail;
  /** Optional API-sourced performance data. When undefined or empty, the
   *  chart renders an empty state — never a fabricated/sample fallback. */
  performanceData?: PerformanceDataPoint[];
  /** Optional API-sourced performance history for the table. */
  performanceHistory?: PerformanceHistoryRow[];
}

export function LeveragedPerfChart({
  fund,
  performanceData,
  performanceHistory,
}: LeveragedPerfChartProps) {
  const [activePeriod, setActivePeriod] = useState<Period>("ALL");
  const [activeView, setActiveView] = useState<"month" | "quarter">("month");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const hoverIdx = useRef(-1);

  // Live API data only — no fabricated/sample fallback. When `performanceData`
  // is undefined or empty, `allData` is `[]` and the canvas renders nothing;
  // the parent block surfaces an empty-state message in place of the chart.
  const allData = useMemo(() => {
    if (!performanceData || performanceData.length === 0) return [] as DataPoint[];
    return performanceData.map((d) => ({
      date: new Date(d.date),
      nav: d.nav,
      mkt: d.mkt,
    }));
  }, [performanceData]);

  const hasChartData = allData.length > 0;

  // Filter by period
  const filteredData = useMemo(() => {
    if (allData.length === 0) return allData;
    const last = allData[allData.length - 1]!.date;
    switch (activePeriod) {
      case "1M": {
        const c = new Date(last);
        c.setMonth(c.getMonth() - 1);
        return allData.filter((d) => d.date >= c);
      }
      case "3M": {
        const c = new Date(last);
        c.setMonth(c.getMonth() - 3);
        return allData.filter((d) => d.date >= c);
      }
      case "1Y": {
        const c = new Date(last);
        c.setFullYear(c.getFullYear() - 1);
        return allData.filter((d) => d.date >= c);
      }
      case "YTD": {
        const c = new Date(last.getFullYear(), 0, 1);
        return allData.filter((d) => d.date >= c);
      }
      default:
        return allData;
    }
  }, [allData, activePeriod]);

  const drawChart = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    // Measure the canvas itself, not its padded wrapper. Earlier we used
    // wrap.getBoundingClientRect() which included the wrap's px-10 padding
    // (80px desktop / 4px mobile), so the canvas was sized 80px wider than
    // its parent's content area and bled past the right edge of the card.
    canvas.style.width = "100%";
    const rect = canvas.getBoundingClientRect();
    const W = rect.width;
    const H = 340;

    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const PAD = { top: 16, right: 12, bottom: 32, left: 52 };
    const plotW = W - PAD.left - PAD.right;
    const plotH = H - PAD.top - PAD.bottom;
    const data = filteredData;

    ctx.clearRect(0, 0, W, H);

    if (data.length === 0) return;

    // Y range
    let minV = Number.POSITIVE_INFINITY;
    let maxV = Number.NEGATIVE_INFINITY;
    for (const d of data) {
      minV = Math.min(minV, d.nav, d.mkt);
      maxV = Math.max(maxV, d.nav, d.mkt);
    }
    const range = maxV - minV || 1;
    const step = niceStep(range);
    const yMin = Math.floor(minV / step) * step - step;
    const yMax = Math.ceil(maxV / step) * step + step;

    const xPx = (i: number) => PAD.left + (i / (data.length - 1 || 1)) * plotW;
    const yPx = (v: number) => PAD.top + plotH * (1 - (v - yMin) / (yMax - yMin));

    // Theme
    const isDark = document.documentElement.getAttribute("data-theme") !== "light";
    const gridC = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)";
    const textC = isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.35)";

    // Grid + Y labels
    for (let v = yMin; v <= yMax; v += step) {
      const y = yPx(v);
      ctx.beginPath();
      ctx.moveTo(PAD.left, y);
      ctx.lineTo(W - PAD.right, y);
      ctx.strokeStyle = gridC;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = textC;
      ctx.font = "11px 'Instrument Sans', sans-serif";
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      ctx.fillText(`$${v.toFixed(0)}`, PAD.left - 8, y);
    }

    // NAV gradient fill
    const grad = ctx.createLinearGradient(0, PAD.top, 0, H - PAD.bottom);
    grad.addColorStop(0, "rgba(255,85,0,0.20)");
    grad.addColorStop(0.5, "rgba(255,85,0,0.04)");
    grad.addColorStop(1, "rgba(255,85,0,0)");
    ctx.beginPath();
    ctx.moveTo(xPx(0), yPx(data[0]!.nav));
    for (let i = 1; i < data.length; i++) ctx.lineTo(xPx(i), yPx(data[i]!.nav));
    ctx.lineTo(xPx(data.length - 1), H - PAD.bottom);
    ctx.lineTo(xPx(0), H - PAD.bottom);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();

    // Market price line
    ctx.beginPath();
    ctx.moveTo(xPx(0), yPx(data[0]!.mkt));
    for (let i = 1; i < data.length; i++) ctx.lineTo(xPx(i), yPx(data[i]!.mkt));
    ctx.strokeStyle = isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)";
    ctx.lineWidth = 1.2;
    ctx.stroke();

    // NAV line
    ctx.beginPath();
    ctx.moveTo(xPx(0), yPx(data[0]!.nav));
    for (let i = 1; i < data.length; i++) ctx.lineTo(xPx(i), yPx(data[i]!.nav));
    ctx.strokeStyle = "#ff5500";
    ctx.lineWidth = 2;
    ctx.stroke();

    // X-axis labels
    const labelCount = W < 500 ? 4 : 7;
    for (let j = 0; j < labelCount; j++) {
      const idx = Math.round((j / (labelCount - 1)) * (data.length - 1));
      const x = xPx(idx);
      const dt = data[idx]!.date;
      ctx.fillStyle = textC;
      ctx.font = "10px 'Instrument Sans', sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(
        `${MONTHS[dt.getMonth()]} '${String(dt.getFullYear()).slice(2)}`,
        x,
        H - PAD.bottom + 8,
      );
    }

    // Hover crosshair + dots
    const hi = hoverIdx.current;
    if (hi >= 0 && hi < data.length) {
      const hx = xPx(hi);
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(hx, PAD.top);
      ctx.lineTo(hx, H - PAD.bottom);
      ctx.stroke();
      ctx.setLineDash([]);

      // NAV dot
      const ny = yPx(data[hi]!.nav);
      ctx.beginPath();
      ctx.arc(hx, ny, 5, 0, Math.PI * 2);
      ctx.fillStyle = isDark ? "#0a0a0a" : "#faf9f7";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(hx, ny, 3.5, 0, Math.PI * 2);
      ctx.fillStyle = "#ff5500";
      ctx.fill();

      // Mkt dot
      const my = yPx(data[hi]!.mkt);
      ctx.beginPath();
      ctx.arc(hx, my, 4, 0, Math.PI * 2);
      ctx.fillStyle = isDark ? "#0a0a0a" : "#faf9f7";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(hx, my, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)";
      ctx.fill();
    }
  }, [filteredData]);

  // Draw on mount, resize, and theme change
  useEffect(() => {
    drawChart();
    const onResize = () => drawChart();
    window.addEventListener("resize", onResize);
    // Container-aware resize so the chart stays in sync when its parent
    // width changes (e.g. sidebar collapse, layout shift) without a window
    // resize event firing.
    const ro = new ResizeObserver(() => drawChart());
    if (canvasRef.current) ro.observe(canvasRef.current);
    const observer = new MutationObserver(() => drawChart());
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => {
      window.removeEventListener("resize", onResize);
      ro.disconnect();
      observer.disconnect();
    };
  }, [drawChart]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const wrap = wrapRef.current;
      const canvas = canvasRef.current;
      const tt = tooltipRef.current;
      if (!wrap || !canvas || !tt || filteredData.length === 0) return;
      // Measure the canvas (not the wrap) so mouseX matches the chart's
      // own pixel coordinates — the wrap has px-10 padding around the
      // canvas, and using the wrap's rect made hover targeting drift by
      // ~40px on desktop.
      const canvasRect = canvas.getBoundingClientRect();
      const mx = e.clientX - canvasRect.left;
      const PAD_L = 52;
      const PAD_R = 12;
      const W = canvasRect.width;
      const plotW = W - PAD_L - PAD_R;

      if (mx < PAD_L || mx > W - PAD_R) {
        hoverIdx.current = -1;
        tt.style.opacity = "0";
        drawChart();
        return;
      }

      const ratio = (mx - PAD_L) / plotW;
      const idx = Math.max(
        0,
        Math.min(filteredData.length - 1, Math.round(ratio * (filteredData.length - 1))),
      );
      hoverIdx.current = idx;

      const d = filteredData[idx]!;
      const dateEl = tt.querySelector<HTMLElement>("[data-tooltip-date]");
      const navEl = tt.querySelector<HTMLElement>("[data-tooltip-nav]");
      const mktEl = tt.querySelector<HTMLElement>("[data-tooltip-mkt]");
      if (dateEl) dateEl.textContent = fmtDate(d.date);
      if (navEl) navEl.textContent = `$${d.nav.toFixed(2)}`;
      if (mktEl) mktEl.textContent = `$${d.mkt.toFixed(2)}`;

      // Tooltip is positioned relative to the wrap (its parent), so add
      // the canvas's left offset within the wrap back in.
      const wrapRect = wrap.getBoundingClientRect();
      const canvasOffsetInWrap = canvasRect.left - wrapRect.left;
      const ttW = 160;
      let left = canvasOffsetInWrap + mx + 16;
      if (left + ttW > wrapRect.width) left = canvasOffsetInWrap + mx - ttW - 16;
      tt.style.left = `${left}px`;
      tt.style.top = "10px";
      tt.style.opacity = "1";
      drawChart();
    },
    [filteredData, drawChart],
  );

  const handleMouseLeave = useCallback(() => {
    hoverIdx.current = -1;
    if (tooltipRef.current) tooltipRef.current.style.opacity = "0";
    drawChart();
  }, [drawChart]);

  return (
    <div
      className="border-b border-[var(--etf-border)]"
      id="sec-performance"
      data-fund-section="showPerformanceChart"
    >
      {/* Section Header */}
      <div className="flex items-center justify-between gap-3 px-10 py-6 border-b border-[var(--etf-border)] max-md:px-4 max-md:py-4 max-md:flex-col max-md:items-start">
        <div className="font-[family-name:var(--font-f37bolton)] text-[24px] tracking-[-0.02em] text-[var(--etf-white)] flex items-center gap-3 leading-none max-md:text-[20px]">
          <span className="inline-block w-[3px] h-[20px] bg-[var(--etf-orange)]" />
          Performance
        </div>
        <div className="font-[family-name:var(--font-geist)] text-[12px] text-[var(--etf-white3)] tracking-[0.08em]">
          NAV vs Market Price &middot; Since Inception
        </div>
      </div>

      {/* Chart — hidden on buffer pages (static buffer renders only the
          Performance History table here, no NAV/Mkt chart). When no live
          performance data is available, render an empty-state message in
          place of the canvas — never a sample/fabricated polyline. */}
      {fund.type !== "buffer" && (
        <div className="border-b border-[var(--etf-border)]">
          {hasChartData ? (
            <>
              {/* Controls */}
              <div className="flex items-center justify-between flex-wrap gap-3 px-10 py-4 border-b border-[var(--etf-border2)] max-md:px-4 max-md:flex-col max-md:items-start">
                <div className="flex gap-5">
                  {/* Canonical perf-chart legend */}
                  <span className="flex items-center gap-1.5 font-[family-name:var(--font-instrument)] text-[12px] leading-[1.5] text-[var(--etf-white3)] tracking-[0.04em]">
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ background: "var(--etf-orange)" }}
                    />
                    NAV
                  </span>
                  <span className="flex items-center gap-1.5 font-[family-name:var(--font-instrument)] text-[12px] leading-[1.5] text-[var(--etf-white3)] tracking-[0.04em]">
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ background: "var(--etf-white)" }}
                    />
                    Market Price
                  </span>
                </div>
                <div className="flex gap-0 border border-[var(--etf-border)] rounded-sm overflow-hidden">
                  {PERIODS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={cn(
                        "bg-transparent border-none border-r border-r-[var(--etf-border)] text-[var(--etf-white3)] font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.1em] px-3.5 py-1.5 cursor-pointer transition-[background,color] duration-150 uppercase",
                        "hover:bg-white/[0.04] hover:text-[var(--etf-white)]",
                        "last:border-r-0",
                        activePeriod === p &&
                          "bg-[var(--etf-orange)] text-white hover:bg-[var(--etf-orange)] hover:text-white",
                      )}
                      onClick={() => setActivePeriod(p)}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              {/* Canvas */}
              <div
                className="relative px-10 py-7 max-md:px-2 max-md:py-4"
                ref={wrapRef}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                style={{ cursor: "crosshair" }}
              >
                <canvas
                  ref={canvasRef}
                  style={{ width: "100%", height: "340px", display: "block" }}
                />
                <div
                  className="absolute pointer-events-none bg-[var(--etf-black2)] border border-[var(--etf-border)] rounded-[4px] px-3.5 py-2.5 min-w-[140px] z-20 shadow-[0_8px_32px_rgba(0,0,0,0.5)]"
                  ref={tooltipRef}
                  style={{ opacity: 0, transition: "opacity 0.1s" }}
                >
                  <div
                    data-tooltip-date
                    className="font-[family-name:var(--font-instrument)] text-[11px] text-[var(--etf-white3)] tracking-[0.06em] mb-2 uppercase"
                  />
                  <div className="flex items-center gap-1.5 font-[family-name:var(--font-dmmono)] text-[12px] text-[var(--etf-white)] mb-[3px]">
                    <span
                      className="w-[6px] h-[6px] rounded-full shrink-0"
                      style={{ background: "var(--etf-orange)" }}
                    />
                    NAV: <span data-tooltip-nav />
                  </div>
                  <div className="flex items-center gap-1.5 font-[family-name:var(--font-dmmono)] text-[12px] text-[var(--etf-white)] mb-[3px]">
                    <span
                      className="w-[6px] h-[6px] rounded-full shrink-0"
                      style={{ background: "var(--etf-white)" }}
                    />
                    Mkt: <span data-tooltip-mkt />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="py-[32px] px-10 mb-8 text-center italic text-[14px] leading-[1.6] text-[var(--etf-white3)] font-[family-name:var(--font-instrument)] border border-[var(--etf-border)] rounded-[2px]">
              Performance information will be available after the Fund has completed one full
              calendar quarter of operations.
            </div>
          )}
        </div>
      )}

      {/* Performance History Table — visible on every leveraged 2x fund
          (canonical static-reference renders the shell with em-dash cells
          even when no real data is yet available, on all 158 etf-*-2x.html
          + etf-fdrx.html files). Hidden for ordinary thematic funds per
          static-reference commit d4c9d81; FDRS is the carve-out (its data
          record sits under `type: "thematic"` but the static reference
          etf-fdrs.html renders the full perf-history table).

          `sinceInceptionAnn` intentionally omitted — 158/158 leveraged 2x
          canonical files render only the `(Cum.)` column. */}
      {(fund.type !== "thematic" || fund.ticker === "FDRS") && (
        <div className="border-b border-[var(--etf-border)]">
          {/* Table Header */}
          <div className="flex items-center justify-between flex-wrap gap-3 px-10 py-4 border-b border-[var(--etf-border2)] max-md:px-4">
            <div className="font-[family-name:var(--font-f37bolton)] text-[20px] leading-[1.5] text-[var(--etf-white)] tracking-[-0.01em]">
              Performance History
            </div>
            <div className="flex gap-0 border border-[var(--etf-border)] rounded-sm overflow-hidden">
              <button
                type="button"
                className={cn(
                  "bg-transparent border-none border-r border-r-[var(--etf-border)] text-[var(--etf-white3)] font-[family-name:var(--font-instrument)] text-[11px] leading-normal tracking-[0.08em] px-4 py-1.5 cursor-pointer transition-[background,color] duration-150",
                  "hover:bg-white/[0.04] hover:text-[var(--etf-white)]",
                  activeView === "month" &&
                    "bg-[rgba(255,85,0,0.12)] text-[var(--etf-orange)] hover:bg-[rgba(255,85,0,0.12)] hover:text-[var(--etf-orange)]",
                )}
                onClick={() => setActiveView("month")}
              >
                Month-End
              </button>
              <button
                type="button"
                className={cn(
                  "bg-transparent border-none text-[var(--etf-white3)] font-[family-name:var(--font-instrument)] text-[11px] leading-normal tracking-[0.08em] px-4 py-1.5 cursor-pointer transition-[background,color] duration-150",
                  "hover:bg-white/[0.04] hover:text-[var(--etf-white)]",
                  activeView === "quarter" &&
                    "bg-[rgba(255,85,0,0.12)] text-[var(--etf-orange)] hover:bg-[rgba(255,85,0,0.12)] hover:text-[var(--etf-orange)]",
                )}
                onClick={() => setActiveView("quarter")}
              >
                Quarter-End
              </button>
            </div>
          </div>
          <div className="font-[family-name:var(--font-instrument)] text-[12px] leading-[1.5] text-[var(--etf-white3)] tracking-[0.04em] px-10 py-2 max-md:px-4">
            {/* "Data as of" prefers the latest point in the live performance
              series, then the static `performanceAsOf` override, then
              `dateLabel`. */}
            Data as of{" "}
            {allData.length > 0
              ? fmtDate(allData[allData.length - 1]!.date)
              : (fund.performanceAsOf ?? fund.dateLabel)}
          </div>
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[600px]">
              <thead>
                <tr>
                  <th className="font-[family-name:var(--font-instrument)] text-[11px] leading-[1.5] tracking-[0.1em] font-medium uppercase text-[var(--etf-white3)] text-left pl-10 pr-3 py-2.5 border-b border-[var(--etf-border2)] whitespace-nowrap max-md:pl-4 max-md:pr-2" />
                  <th className="font-[family-name:var(--font-instrument)] text-[11px] leading-[1.5] tracking-[0.1em] font-medium uppercase text-[var(--etf-white3)] text-right px-3 py-2.5 border-b border-[var(--etf-border2)] whitespace-nowrap max-md:px-2">
                    1-Month
                  </th>
                  <th className="font-[family-name:var(--font-instrument)] text-[11px] leading-[1.5] tracking-[0.1em] font-medium uppercase text-[var(--etf-white3)] text-right px-3 py-2.5 border-b border-[var(--etf-border2)] whitespace-nowrap max-md:px-2">
                    3-Month
                  </th>
                  <th className="font-[family-name:var(--font-instrument)] text-[11px] leading-[1.5] tracking-[0.1em] font-medium uppercase text-[var(--etf-white3)] text-right px-3 py-2.5 border-b border-[var(--etf-border2)] whitespace-nowrap max-md:px-2">
                    YTD
                  </th>
                  <th className="font-[family-name:var(--font-instrument)] text-[11px] leading-[1.5] tracking-[0.1em] font-medium uppercase text-[var(--etf-white3)] text-right px-3 py-2.5 border-b border-[var(--etf-border2)] whitespace-nowrap max-md:px-2">
                    1-Year
                  </th>
                  <th className="font-[family-name:var(--font-instrument)] text-[11px] leading-[1.5] tracking-[0.1em] font-medium uppercase text-[var(--etf-white3)] text-right px-3 py-2.5 border-b border-[var(--etf-border2)] whitespace-nowrap max-md:px-2">
                    3-Year
                  </th>
                  <th className="font-[family-name:var(--font-instrument)] text-[11px] leading-[1.5] tracking-[0.1em] font-medium uppercase text-[var(--etf-white3)] text-right px-3 py-2.5 border-b border-[var(--etf-border2)] whitespace-nowrap max-md:px-2">
                    5-Year
                  </th>
                  {/* `Since Inception (Ann.)` removed — 158/158 leveraged
                      2x canonical files render only the `(Cum.)` column.
                      `PerformanceHistoryRow.sinceInceptionAnn` is still
                      computed for forward-compat; just not surfaced here. */}
                  <th className="font-[family-name:var(--font-instrument)] text-[11px] leading-[1.5] tracking-[0.1em] font-medium uppercase text-[var(--etf-white3)] text-right px-3 py-2.5 border-b border-[var(--etf-border2)] whitespace-nowrap max-md:px-2">
                    Since Inception (Cum.)
                  </th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const rows: { label: string; cells: string[] }[] | null = performanceHistory
                    ? performanceHistory.map((row) => ({
                        label: row.label,
                        cells: [
                          row.oneMonth,
                          row.threeMonth,
                          row.ytd,
                          row.oneYear,
                          row.threeYear,
                          row.fiveYear,
                          row.sinceInceptionCum,
                        ],
                      }))
                    : null;
                  if (rows) {
                    return rows.map((row, i) => (
                      <tr key={i}>
                        <td className="font-[family-name:var(--font-instrument)] text-[12px] leading-[1.5] tracking-[0.04em] font-medium text-[var(--etf-white2)] text-left pl-10 pr-3 py-3.5 border-b border-[var(--etf-border2)] whitespace-nowrap max-md:pl-4 max-md:pr-2">
                          {row.label}
                        </td>
                        {row.cells.map((cell, ci) => {
                          const isMissing = cell === "—" || cell === "N/A";
                          const isNeg =
                            !isMissing && (cell.startsWith("-") || cell.startsWith("−"));
                          const isPos = !isMissing && cell.startsWith("+");
                          return (
                            <td
                              key={ci}
                              className={cn(
                                "font-[family-name:var(--font-dmmono)] text-[13px] leading-[1.5] text-right pl-3 pr-3 py-3.5 border-b border-[var(--etf-border2)] whitespace-nowrap max-md:pl-2 max-md:pr-2",
                                isMissing && "text-[var(--etf-white3)] opacity-50 italic",
                                isNeg && "text-[var(--etf-red)]",
                                isPos && "text-[var(--etf-green)]",
                                !isMissing && !isNeg && !isPos && "text-[var(--etf-white)]",
                              )}
                            >
                              {cell}
                            </td>
                          );
                        })}
                      </tr>
                    ));
                  }
                  return (
                    <>
                      <tr>
                        <td className="font-[family-name:var(--font-instrument)] text-[12px] leading-[1.5] tracking-[0.04em] font-medium text-[var(--etf-white2)] text-left pl-10 pr-3 py-3.5 border-b border-[var(--etf-border2)] whitespace-nowrap max-md:pl-4 max-md:pr-2">
                          NAV
                        </td>
                        {Array.from({ length: 7 }).map((_, i) => (
                          <td
                            key={i}
                            className="font-[family-name:var(--font-dmmono)] text-[13px] leading-[1.5] text-[var(--etf-white3)] text-right pl-3 pr-3 py-3.5 border-b border-[var(--etf-border2)] opacity-50 italic whitespace-nowrap max-md:pl-2 max-md:pr-2"
                          >
                            &mdash;
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="font-[family-name:var(--font-instrument)] text-[12px] leading-[1.5] tracking-[0.04em] font-medium text-[var(--etf-white2)] text-left pl-10 pr-3 py-3.5 border-b border-[var(--etf-border2)] whitespace-nowrap max-md:pl-4 max-md:pr-2">
                          Market Price
                        </td>
                        {Array.from({ length: 7 }).map((_, i) => (
                          <td
                            key={i}
                            className="font-[family-name:var(--font-dmmono)] text-[13px] leading-[1.5] text-[var(--etf-white3)] text-right pl-3 pr-3 py-3.5 border-b border-[var(--etf-border2)] opacity-50 italic whitespace-nowrap max-md:pl-2 max-md:pr-2"
                          >
                            &mdash;
                          </td>
                        ))}
                      </tr>
                    </>
                  );
                })()}
              </tbody>
            </table>
          </div>
          {/* Disclaimer */}
          <div className="font-[family-name:var(--font-instrument)] text-[11px] text-[var(--etf-white3)] leading-[1.7] px-10 py-7 opacity-[0.65] italic max-md:px-4">
            Performance data quoted represents past performance, which is no guarantee of future
            results. Current performance may be lower or higher than the performance data quoted.
            Investment return and principal value will fluctuate so that an investor&rsquo;s shares,
            when redeemed, may be worth more or less than the original cost. Returns for periods of
            less than one year are not annualized.
          </div>
        </div>
      )}
    </div>
  );
}
